/**
 * Read-only access to the "is this navigation a modal?" signal.
 *
 * The reflection from `history.state` into the underlying `ref` happens in
 * `plugins/modal-navigation.client.ts` (it imports `modalNavigationRef` from
 * here and writes to it on `router.afterEach`). This composable just exposes
 * computed views so consumers subscribe reactively without touching storage.
 *
 * Replacement for the legacy `useNavigationContext().isInternalNavigation`:
 *   - Old: read a global plain object, with a `route.path` access used as a
 *     fake reactive dependency (broke under prod scheduling).
 *   - New: read a real ref written from `router.afterEach`, so the value the
 *     layout reads is the value that changes — and it changes *after* the
 *     route has committed, so `route.params` and `isModalNavigation` are
 *     always in sync within the same render tick.
 *
 * SSR: returns `false` on the server. There is no history stack during SSR,
 * which is exactly the right answer for a fresh server response: render the
 * post-detail route as a full page, not a modal.
 */
import { computed, ref, type Ref } from 'vue'

import type { ModalNavigationContext, UseModalNavigationReturn } from '../types/navigation'

/**
 * Module-scoped ref shared with the modal-navigation plugin. The plugin is
 * the sole writer (from `router.afterEach`); this composable is the sole
 * reader path. Exporting it here (alongside the composable) keeps the test
 * surface small and avoids a circular import via the plugin file.
 */
export const modalNavigationRef: Ref<ModalNavigationContext> = ref({})

export const useModalNavigation = (): UseModalNavigationReturn => {
  return {
    isModalNavigation: computed(() => Boolean(modalNavigationRef.value.background)),
    modalBackgroundPath: computed(() => modalNavigationRef.value.background),
    // `useRoute()`'s reactive proxy in this codebase fires only on the first
    // navigation (confirmed via paired watchers — see `useModalNavigation` JSDoc
    // and the layout's `isLayoutPostModalRoute` comment). These computeds are
    // populated from the `to` route in `router.afterEach`, which mirrors the
    // working `router.currentRoute` and is fresh on every navigation. Use these
    // wherever a persistent layout needs reactive route data.
    modalPostId: computed(() => modalNavigationRef.value.currentPostId),
    modalFullPath: computed(() => modalNavigationRef.value.currentFullPath),
    modalGroupId: computed(() => modalNavigationRef.value.currentGroupId),
    modalLocationId: computed(() => modalNavigationRef.value.currentLocationId),
  }
}
